import type { NleClipInfo, NleClipInfoMap } from '@mimir/client-common/fileSync/premiereTypes';
import type { SequenceExportInfo } from '@mimir/primitive/nle/sequence';
import type { NleProject } from '@mimir/primitive/nle/project';
import { computed, reactive, vueUpdateObjectContent } from '../compat/vueCompat';
import isEmpty from 'lodash/isEmpty';
import { getBasenameWithoutExtension } from '@mimir/common/platformAbstraction/pathComposition';
import { getGlobalStore } from '../store';


interface PremiereState {
  clipInfos: NleClipInfoMap;
  refreshingAllClipInfos: boolean;
  refreshingAllLocalMediaStates: boolean;
  relinkingAllClips: boolean;
  syncPageSelectedFilterId: string;
  orderedOpenProjects: NleProject[];
  activeSequenceInfo: Partial<SequenceExportInfo>;
  isGeneratingVirtualClipAaf: boolean;
  insertIntoSequence: boolean;
  newSubclipName: string;
  subclipRenameMenuVisible: boolean;
  projectChangedEventLastFired: number;
}

export const premiere: PremiereState = reactive({
  clipInfos: {},
  refreshingAllClipInfos: false,
  refreshingAllLocalMediaStates: false,
  relinkingAllClips: false,
  syncPageSelectedFilterId: 'noFilter',
  orderedOpenProjects: [],
  activeSequenceInfo: {},
  isGeneratingVirtualClipAaf: false,
  insertIntoSequence: false,
  newSubclipName: '',
  subclipRenameMenuVisible: false,
  projectChangedEventLastFired: 0,
});


export const clipInfosArray = computed(() => Object.values(premiere.clipInfos));
export const bulkClipUpdateGoingOn = computed(() => premiere.refreshingAllClipInfos || premiere.refreshingAllLocalMediaStates || premiere.relinkingAllClips);

export const clipKeysForActiveSequence = computed(() => {
  const clipKeys = new Set<string | undefined>();
  if (!premiere.activeSequenceInfo || !premiere.activeSequenceInfo.tracks) {
    return clipKeys;
  }

  premiere.activeSequenceInfo.tracks.forEach(track => track.clips.forEach(clip => clipKeys.add(clip.key)));
  return clipKeys;
});


export function setPremiereClipInfos(clipInfos: NleClipInfoMap) {
  vueUpdateObjectContent(premiere.clipInfos, clipInfos);
}


export function updatePremiereClipInfo(clipInfo: NleClipInfo) {
  // Since clipInfo may be a different object than the one in premiere.clipInfos[clipInfo.key],
  // we cannot use vueSet since it may replace the existing object reference and that would break
  // reactivity for UI components that are observing the original clipInfo object. Instead,
  // we replace the original of the original object with the content of the given one
  vueUpdateObjectContent(premiere.clipInfos[clipInfo.key], clipInfo);
}


export interface SyncPageFilter {
  // id is stable across generations
  id: string;

  // changes every time filters are generated (when project list changes)
  lastGenerationTimestamp?: string;

  // key is `id|timestamp`, changes every time filters are generated (when project list changes), with a new timestamp
  key?: string;

  name: string;
  tooltip: string;
  matchesCondition: (clipInfo: NleClipInfo) => boolean;
  iconImagePath?: string;
  icon?: string;
}


const fixedFilters: SyncPageFilter[] = [
  {
    id: 'noFilter',
    name: 'All clips',
    tooltip: 'All clips from all open projects',
    matchesCondition: (clipInfo: NleClipInfo) => true,
    icon: 'list',
  },
];


export const getSyncPageFilterKey = (filterId: string, timestamp: string) => `${filterId}|${timestamp}`;


export const syncPageFilters = computed(() => {
  let result = [...fixedFilters];
  if (!isEmpty(premiere.activeSequenceInfo?.sequenceName)) {
    const sequenceName = premiere.activeSequenceInfo.sequenceName!;
    const activeSequenceFilter = {
      id: 'activeSequenceFilter',
      name: sequenceName,
      tooltip: `Active sequence: ${sequenceName}`,
      matchesCondition: (clipInfo: NleClipInfo) => clipKeysForActiveSequence.value.has(clipInfo.key),
      iconImagePath: '/assets/icons/premiere-sequence.png',
    };
    result.push(activeSequenceFilter);
  }

  // Since "syncPageFilters" is a computed property, it would require non trivial changes
  // to inject the application context to get the path separator from it. However, since
  // this computed property is only used in Premiere, let's apply here the same test
  // as the one performed in getApplicationContext() when we know we are inside an Adobe panel
  const isWindows = (navigator.platform == 'Win32') || (navigator.platform == 'Windows');
  const pathSep = isWindows ? '\\' : '/';

  const projectFilters = premiere.orderedOpenProjects.map(p => {
    return {
      id: `project-${p.path}`,
      name: getBasenameWithoutExtension(p.name, pathSep),
      tooltip: p.path,
      matchesCondition: (clipInfo: NleClipInfo) => clipInfo.projectPath === p.path,
      iconImagePath: `/assets/icons/${getGlobalStore().getters.dvrMode ? 'dvr-logo.png' : 'premiere-logo.svg'}`,
    };
  });
  result = result.concat(projectFilters);

  // make keys (used for vue components like tabs) unique, otherwise the order of vuetify tabs becomes indeterminate
  const ts = `${new Date().getTime()}`;
  for (let i = 0; i < result.length; i++) {
    result[i].lastGenerationTimestamp = ts;
    result[i].key = getSyncPageFilterKey(result[i].id, ts);
  }

  return result;
});
